import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, TrendingUp } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { SalesTrendData } from '../../hooks/useDashboardStats';

interface SalesChartProps {
  data: SalesTrendData[] | undefined;
  isLoading?: boolean;
  period: 'week' | 'month';
  onPeriodChange: (period: 'week' | 'month') => void;
  className?: string;
}

function SalesChartComponent({
  data,
  isLoading = false,
  period,
  onPeriodChange,
  className,
}: SalesChartProps) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const isRTL = i18n.dir() === 'rtl';
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Calculate chart dimensions and data
  const chartData = useMemo(() => {
    // Safety check: ensure data is an array
    if (!data || !Array.isArray(data) || data.length === 0) {
      return { bars: [], maxValue: 0, labels: [] };
    }

    const maxValue = Math.max(
      ...data.map((d) => Math.max(d.sales || 0, d.purchases || 0)),
      1
    );

    const bars = data.map((item, index) => ({
      ...item,
      salesHeight: (item.sales / maxValue) * 100,
      purchasesHeight: (item.purchases / maxValue) * 100,
      index,
    }));

    // Format date labels based on period
    const labels = data.map((item) => {
      const date = new Date(item.date);
      if (period === 'week') {
        return date.toLocaleDateString(i18n.language, { weekday: 'short' });
      }
      return date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
    });

    return { bars, maxValue, labels };
  }, [data, period, i18n.language]);

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Calculate totals
  const totals = useMemo(() => {
    if (!data || data.length === 0) {
      return { sales: 0, purchases: 0 };
    }
    return data.reduce(
      (acc, item) => ({
        sales: acc.sales + item.sales,
        purchases: acc.purchases + item.purchases,
      }),
      { sales: 0, purchases: 0 }
    );
  }, [data]);

  return (
    <div
      className={cn(
        'bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6 shadow-sm',
        className
      )}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary-50 dark:bg-primary-900/20 p-2">
            <BarChart3 className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
              {t('charts.salesTrend')}
            </h3>
            {!isLoading && (
              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                {t('charts.salesTotal')}: {formatCurrency(totals.sales)}
              </p>
            )}
          </div>
        </div>

        {/* Period toggle */}
        <div className="flex items-center gap-2" role="group" aria-label={t('charts.periodSelector')}>
          <button
            type="button"
            onClick={() => onPeriodChange('week')}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              period === 'week'
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'text-secondary-600 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-700'
            )}
            aria-pressed={period === 'week'}
          >
            {t('charts.last7Days')}
          </button>
          <button
            type="button"
            onClick={() => onPeriodChange('month')}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              period === 'month'
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'text-secondary-600 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-700'
            )}
            aria-pressed={period === 'month'}
          >
            {t('charts.last30Days')}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500" aria-hidden="true" />
          <span className="text-sm text-secondary-600 dark:text-secondary-400">
            {t('stats.todaySales')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-500" aria-hidden="true" />
          <span className="text-sm text-secondary-600 dark:text-secondary-400">
            {t('stats.todayPurchases')}
          </span>
        </div>
      </div>

      {/* Chart area */}
      <div className="relative h-64">
        {isLoading ? (
          <div className="flex items-end justify-between h-full gap-2 px-2">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-t animate-pulse"
                  style={{ height: `${Math.random() * 60 + 20}%` }}
                />
                <div className="h-4 w-8 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mt-2" />
              </div>
            ))}
          </div>
        ) : data && Array.isArray(data) && data.length > 0 ? (
          <div
            className={cn(
              'flex items-end justify-between h-full gap-1 sm:gap-2 px-1 sm:px-2',
              isRTL && 'flex-row-reverse'
            )}
            role="img"
            aria-label={t('charts.salesTrend')}
          >
            {chartData.bars.map((bar, index) => (
              <div
                key={bar.date}
                className="flex-1 flex flex-col items-center min-w-0"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Tooltip */}
                {hoveredIndex === index && (
                  <div
                    className={cn(
                      'absolute bottom-full mb-2 px-3 py-2 bg-secondary-900 dark:bg-secondary-100 text-white dark:text-secondary-900 text-xs rounded-lg shadow-lg z-10 whitespace-nowrap',
                      isRTL ? 'text-right' : 'text-left'
                    )}
                    role="tooltip"
                  >
                    <div className="font-medium mb-1">{chartData.labels[index]}</div>
                    <div className="text-green-300 dark:text-green-600">
                      {t('stats.todaySales')}: {formatCurrency(bar.sales)}
                    </div>
                    <div className="text-blue-300 dark:text-blue-600">
                      {t('stats.todayPurchases')}: {formatCurrency(bar.purchases)}
                    </div>
                  </div>
                )}

                {/* Bars container */}
                <div className="flex items-end gap-0.5 sm:gap-1 h-48 w-full">
                  {/* Sales bar */}
                  <div
                    className={cn(
                      'flex-1 bg-green-500 rounded-t transition-all duration-300 min-h-[4px]',
                      hoveredIndex === index && 'bg-green-600'
                    )}
                    style={{ height: `${bar.salesHeight}%` }}
                    role="presentation"
                  />
                  {/* Purchases bar */}
                  <div
                    className={cn(
                      'flex-1 bg-blue-500 rounded-t transition-all duration-300 min-h-[4px]',
                      hoveredIndex === index && 'bg-blue-600'
                    )}
                    style={{ height: `${bar.purchasesHeight}%` }}
                    role="presentation"
                  />
                </div>

                {/* Label */}
                <span
                  className={cn(
                    'mt-2 text-xs text-secondary-500 dark:text-secondary-400 truncate w-full text-center',
                    hoveredIndex === index && 'text-secondary-900 dark:text-secondary-100 font-medium'
                  )}
                >
                  {chartData.labels[index]}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-secondary-500 dark:text-secondary-400">
            <TrendingUp className="h-12 w-12 mb-3 text-secondary-300 dark:text-secondary-600" aria-hidden="true" />
            <p>{t('common:noData')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export const SalesChart = memo(SalesChartComponent);
export default SalesChart;
